/**
 * FlowVariableMentionNode - Tiptap extension for @flowVariable mentions
 *
 * Creates inline atom nodes for V2 flow variable references (e.g., @user_goal, @user_goal.label)
 * Renders as styled green chips in the editor, serializes to @ref format.
 *
 * Behavior on selection: Delete or Select (like CountdownMentionNode, NOT like LinkMentionNode).
 * Follows CountdownMentionNode pattern exactly.
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { FlowVariableMentionChip } from '../components/FlowVariableMentionChip';

export interface FlowVariableMentionNodeAttrs {
  /** Variable reference (e.g., "user_goal" or "user_goal.label") */
  ref: string;
  /** Optional label tag wrapping this mention */
  labelTag: string | null;
}

export const FlowVariableMentionNode = Node.create({
  name: 'flowVariableMention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      ref: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-flow-var-ref'),
        renderHTML: (attributes) => ({
          'data-flow-var-ref': attributes.ref,
        }),
      },
      labelTag: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label-tag'),
        renderHTML: (attributes) => {
          if (!attributes.labelTag) return {};
          return { 'data-label-tag': attributes.labelTag };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-flow-var-ref]',
        getAttrs: (domNode) => {
          const dom = domNode as HTMLElement;
          return {
            ref: dom.getAttribute('data-flow-var-ref'),
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'flow-variable-mention',
        'data-type': 'flowVariableMention',
      }),
      `@${HTMLAttributes['data-flow-var-ref'] || ''}`,
    ];
  },

  /**
   * Serialize to plain text format: @ref
   * Used when extracting text content from the editor for storage
   */
  renderText({ node }) {
    return `@${node.attrs.ref || ''}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(FlowVariableMentionChip);
  },
});

export default FlowVariableMentionNode;
